import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import ProductImage from "./ProductImage";
import productData from "../Datas/productData";
import {
  formatPrice,
  getCartItemKey,
  getCartLineTotal,
} from "../Utils/product";
import "../css/checkout.css";

const STANDARD_SHIPPING = 9.99;
const LAST_ORDER_KEY = "sickzuno:lastOrder";
const AVAILABLE_COUNTRIES = [
  "Australia", "Austria", "Belgium", "Canada", "Denmark", "Finland",
  "France", "Germany", "Hong Kong", "Ireland", "Italy", "Japan",
  "Luxembourg", "Malaysia", "Mexico", "Netherlands", "New Zealand",
  "Norway", "Portugal", "Singapore", "South Korea", "Spain", "Sweden",
  "Switzerland", "Thailand", "United Kingdom", "United States",
];

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  country: "United States",
  region: "",
  postalCode: "",
};

const createOrderNumber = () => {
  const random = Math.floor(100000000000 + Math.random() * 900000000000);
  return `${Date.now()}${random}`;
};

const scrollToTop = () =>
  window.scrollTo({ top: 0, behavior: "smooth" });

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const location = useLocation();
  const [form, setForm] = useState(initialForm);

  const orderItems = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find((entry) => entry.id === item.id);
          if (!product) return null;

          return {
            ...item,
            product,
            lineTotal: getCartLineTotal(product, item),
          };
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = orderItems.reduce(
    (total, item) => total + item.lineTotal,
    0
  );
  const discountRate =
    location.state?.discountCode === "SICKZUNO10" ? 0.1 : 0;
  const discount = subtotal * discountRate;
  const shipping = STANDARD_SHIPPING;
  const total = Math.max(0, subtotal - discount + shipping);

  const updateForm = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!orderItems.length) return;

    const orderNumber = createOrderNumber();
    const order = {
      orderNumber,
      email: form.email,
      itemCount: orderItems.reduce(
        (count, item) => count + item.quantity,
        0
      ),
      total,
      createdAt: new Date().toISOString(),
    };

    try {
      window.sessionStorage.setItem(LAST_ORDER_KEY, JSON.stringify(order));
    } catch (error) {
      console.warn("Unable to save the order confirmation.", error);
    }

    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: "USD",
      email: form.email,
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: orderItems[0].product.name,
    });
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (orderItems.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty" id="main-content">
        <section className="checkout-empty" aria-labelledby="checkout-empty-title">
          <span>Checkout</span>
          <h1 id="checkout-empty-title">Your cart is ready for a refill</h1>
          <p>
            Add an ingredient before starting checkout.
          </p>
          <div className="checkout-empty__actions">
            <Link className="checkout-button" to="/shop" onClick={scrollToTop}>
              Browse ingredients
            </Link>
            <Link className="checkout-link-button" to="/cartpage" onClick={scrollToTop}>
              Return to cart
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page" id="main-content">
      <div className="checkout-page__shell">
        <header className="checkout-header">
          <div>
            <span className="checkout-eyebrow">sickzuno</span>
            <h1>Secure checkout</h1>
          </div>
          <Link to="/cartpage" onClick={scrollToTop}>
            ← Return to cart
          </Link>
        </header>

        <div className="checkout-demo-notice" role="note">
          <strong>Secure checkout.</strong>
          <span>Enter delivery details below. Payment is completed on our secure payment page.</span>
        </div>

        <form className="checkout-layout" onSubmit={handleSubmit}>
          <div className="checkout-form-column">
            <section className="checkout-section" aria-labelledby="contact-title">
              <div className="checkout-section__heading">
                <span>01</span>
                <div>
                  <h2 id="contact-title">Contact</h2>
                  <p>Used for your order confirmation and delivery updates.</p>
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-email">Email address</label>
                <input
                  id="checkout-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={updateForm}
                  placeholder="cook@example.com"
                  autoComplete="email"
                  required
                />
              </div>
            </section>

            <section className="checkout-section" aria-labelledby="delivery-title">
              <div className="checkout-section__heading">
                <span>02</span>
                <div>
                  <h2 id="delivery-title">Delivery details</h2>
                  <p>Provide the address where this order should be delivered.</p>
                </div>
              </div>

              <div className="checkout-field-grid checkout-field-grid--two">
                <div className="checkout-field">
                  <label htmlFor="checkout-first-name">First name</label>
                  <input
                    id="checkout-first-name"
                    name="firstName"
                    type="text"
                    value={form.firstName}
                    onChange={updateForm}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-last-name">Last name</label>
                  <input
                    id="checkout-last-name"
                    name="lastName"
                    type="text"
                    value={form.lastName}
                    onChange={updateForm}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-company">
                  Company <span>(optional)</span>
                </label>
                <input
                  id="checkout-company"
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={updateForm}
                  autoComplete="organization"
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-address">Street address</label>
                <input
                  id="checkout-address"
                  name="address"
                  type="text"
                  value={form.address}
                  onChange={updateForm}
                  placeholder="123 Market Street"
                  autoComplete="address-line1"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-apartment">
                  Apartment, suite, etc. <span>(optional)</span>
                </label>
                <input
                  id="checkout-apartment"
                  name="apartment"
                  type="text"
                  value={form.apartment}
                  onChange={updateForm}
                  autoComplete="address-line2"
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-country">Country / region</label>
                <select
                  id="checkout-country"
                  name="country"
                  value={form.country}
                  onChange={updateForm}
                  autoComplete="country-name"
                  required
                >
                  {AVAILABLE_COUNTRIES.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div className="checkout-field-grid checkout-field-grid--three">
                <div className="checkout-field">
                  <label htmlFor="checkout-city">City</label>
                  <input
                    id="checkout-city"
                    name="city"
                    type="text"
                    value={form.city}
                    onChange={updateForm}
                    autoComplete="address-level2"
                    required
                  />
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-region">State / province</label>
                  <input
                    id="checkout-region"
                    name="region"
                    type="text"
                    value={form.region}
                    onChange={updateForm}
                    autoComplete="address-level1"
                    required
                  />
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-postal-code">Postal code</label>
                  <input
                    id="checkout-postal-code"
                    name="postalCode"
                    type="text"
                    value={form.postalCode}
                    onChange={updateForm}
                    autoComplete="postal-code"
                    required
                  />
                </div>
              </div>

            </section>

            <section className="checkout-section" aria-labelledby="shipping-title">
              <div className="checkout-section__heading">
                <span>03</span>
                <div>
                  <h2 id="shipping-title">Shipping method</h2>
                  <p>Tracked standard delivery is charged at a flat rate.</p>
                </div>
              </div>

              <label className="checkout-choice">
                <input type="radio" name="shippingMethod" defaultChecked />
                <span className="checkout-choice__copy">
                  <strong>Standard shipping</strong>
                  <small>
                    Estimated delivery timing is shown by the carrier.
                  </small>
                </span>
                <strong>{shipping === 0 ? "Free" : formatPrice(shipping)}</strong>
              </label>
            </section>

            <section className="checkout-section" aria-labelledby="payment-title">
              <div className="checkout-section__heading">
                <span>04</span>
                <div>
                  <h2 id="payment-title">Payment</h2>
                  <p>No card details are collected on this page.</p>
                </div>
              </div>

              <label className="checkout-choice checkout-choice--payment">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="secure"
                  defaultChecked
                  aria-describedby="demo-payment-description"
                />
                <span className="checkout-choice__copy">
                  <strong>Secure online payment</strong>
                  <small id="demo-payment-description">
                    Continue to the hosted payment page after reviewing your order.
                  </small>
                </span>
                <span className="checkout-demo-badge">SECURE</span>
              </label>

              <div className="checkout-payment-message">
                <span aria-hidden="true">✓</span>
                <p>
                  Card information is entered only after you select Pay now and
                  is not collected by this checkout form.
                </p>
              </div>

            </section>

            <button className="checkout-button checkout-submit" type="submit">
              Pay now · {formatPrice(total)}
            </button>
            <p className="checkout-submit-note">
              By continuing, you agree to the Terms &amp; Conditions and Privacy Policy.
            </p>
          </div>

          <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
            <h2 id="checkout-summary-title">Order summary</h2>
            <div className="checkout-summary__items">
              {orderItems.map((item) => (
                <article className="checkout-item" key={getCartItemKey(item)}>
                  <div className="checkout-item__visual">
                    <ProductImage
                      product={item.product}
                      className="checkout-item__image"
                    />
                    <span aria-label={`Quantity ${item.quantity}`}>
                      {item.quantity}
                    </span>
                  </div>
                  <div className="checkout-item__copy">
                    <h3>{item.product.name}</h3>
                    <p>
                      {[item.variant, item.packaging].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <strong>{formatPrice(item.lineTotal)}</strong>
                </article>
              ))}
            </div>

            <dl className="checkout-summary__totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div className="checkout-summary__discount">
                  <dt>SICKZUNO10</dt>
                  <dd>−{formatPrice(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Shipping</dt>
                <dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd>
              </div>
              <div className="checkout-summary__total">
                <dt>Total <small>USD</small></dt>
                <dd>{formatPrice(total)}</dd>
              </div>
            </dl>

            <p className="checkout-summary__guarantee">
              Carefully packed ingredients · Secure payment · Tracked delivery
            </p>
          </aside>
        </form>
      </div>
    </main>
  );
};

export default CheckOut;
